import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useMotionValue, motion } from 'framer-motion';
import { Play, Pause, SkipBack, SkipForward, Heart, Link as LinkIcon } from 'lucide-react';
import ReactPlayer from 'react-player';


import Knob from './UI/Knob';
import Switch from './UI/Switch';
import VUMeter from './UI/VUMeter';
import { urlFor, fetchWithCache } from '../sanityClient';
import logo from '../assets/logo.png';

// --- STYLED COMPONENTS (DESKTOP) ---

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const MobileWrapper = styled.div`
  display: block;
  width: 100%;
  height: 100%;
  @media (min-width: 1024px) {
    display: none;
  }
`;

const DesktopReceiver = styled.div`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 580px; /* Prevent layout collapse */
    
    /* Matte Off-White / Warm Grey Base */
    background-color: #e4e2db;
    
    /* Subtle noise texture */
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.08'/%3E%3C/svg%3E");
    
    /* Deep Bezel/Shadow for depth */
    box-shadow: 
        inset 0 0 0 2px rgba(0,0,0,0.05),
        inset 0 0 30px rgba(0,0,0,0.05);
    
    padding: 24px 32px; /* Reduced Padding */
    box-sizing: border-box;
    font-family: 'Inter', sans-serif;
    color: #444;
    position: relative;
    border-radius: 4px; /* Slight rounding for main chassis */
  }
`;

// --- DECORATIVE ELEMENTS ---

const Screw = styled.div`
    position: absolute;
    width: 10px;
    height: 10px;
    background: radial-gradient(circle at 30% 30%, #eee, #bbb);
    border-radius: 50%;
    box-shadow: 
        inset 0 1px 2px rgba(255,255,255,0.9), 
        0 1px 1px rgba(0,0,0,0.2);
    z-index: 10;
    
    /* Screw Head Slot */
    &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 80%;
        height: 1.5px;
        background: #999;
        transform: translate(-50%, -50%) rotate(${props => props.rotate || '45deg'});
    }
`;

const VentGrill = styled.div`
    position: absolute;
    top: 20px;
    right: 20px;
    width: 60px;
    height: 100px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    opacity: 0.15;
    pointer-events: none;
`;

const VentSlot = styled.div`
    width: 100%;
    height: 1.5px;
    background: #444;
    border-radius: 1px;
`;

// --- ANIMATIONS ---
const pulseGlow = keyframes`
  0% { box-shadow: inset 0 0 15px rgba(255, 240, 200, 0.1); }
  50% { box-shadow: inset 0 0 25px rgba(255, 230, 180, 0.25); }
  100% { box-shadow: inset 0 0 15px rgba(255, 240, 200, 0.1); }
`;



// --- TUNER DISPLAY COMPONENTS (LIGHT THEME) ---

const TunerDisplay = styled.div`
    width: 100%;
    height: 120px;
    background: #e6e4dc;
    border-radius: 4px;
    margin-bottom: 24px;
    position: relative;
    /* Pulsing Warm Glow */
    animation: ${pulseGlow} 4s ease-in-out infinite;
    
    border: 1px solid rgba(0,0,0,0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;

    /* Top-down Light Leak Effect */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 60px;
        background: linear-gradient(to bottom, rgba(255, 230, 180, 0.4), transparent);
        pointer-events: none;
        z-index: 10;
        mix-blend-mode: multiply;
    }
    /* Bright strip exactly at top edge */
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: rgba(255, 240, 200, 0.8);
        box-shadow: 0 2px 10px rgba(255, 200, 100, 0.5);
        pointer-events: none;
        z-index: 11;
    }
`;

const TunerGlass = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%);
    pointer-events: none;
    z-index: 10;
    mix-blend-mode: overlay;
`;

const TunerScale = styled.div`
    flex: 1;
    position: relative;
    background: transparent;
    display: flex;
    align-items: flex-end;
    padding-bottom: 0px;
`;

// Marker Lines
const ScaleLines = styled.div`
    width: 100%;
    height: 30px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    border-bottom: 1.5px solid #444; /* Dark line */
    padding-bottom: 0px;
    position: relative;
    margin: 0 40px; /* Indent scale */
`;

const ScaleTick = styled.div`
    width: 1.5px;
    background: #444; /* Dark ticks */
    height: ${props => props.major ? '12px' : '6px'};
    opacity: ${props => props.active ? '1' : '0.7'};
`;

const TunerNeedle = styled(motion.div)`
    position: absolute;
    top: 10px;
    bottom: 0;
    width: 2px;
    background: #cc3300; /* Braun Orange/Red */
    z-index: 5;
    transform-origin: center;
    box-shadow: 1px 0 2px rgba(0,0,0,0.2);
    
    /* Needle Cap/Indicator */
    &::after {
        content: '';
        position: absolute;
        top: -4px;
        left: -3px;
        width: 8px;
        height: 8px;
        background: #cc3300;
        border-radius: 50%;
    }
`;

// Digital Readout Area
const TunerInfo = styled.div`
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    font-family: 'Inter', sans-serif; /* Clean sans */
    font-weight: 600;
    font-size: 11px;
    color: #444; /* Dark text */
    letter-spacing: 1px;
    opacity: 0.8;
`;

// --- LAYOUT AREAS ---
const ControlPanel = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    grid-template-areas:
        "meters voltune"
        "transport transport";
    gap: 0px; /* Gaps handled by padding/borders */
    flex: 1;
    width: 100%;
    max-width: 100%;
    overflow: visible;
    background: #e4e2db;
    border-radius: 4px;
    border: 1px solid rgba(0,0,0,0.05); /* Subtle container border */
`;

const PanelSection = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    padding: 16px; 

    /* Assign Grid Areas based on child index */
    &:nth-child(1) {
        grid-area: meters;
        border-right: 1px solid rgba(0,0,0,0.05);
        border-bottom: 1px solid rgba(0,0,0,0.05);
    }
    
    &:nth-child(2) {
        grid-area: transport;
        padding-top: 24px;
    }
    
    &:nth-child(3) {
        grid-area: voltune;
        border-bottom: 1px solid rgba(0,0,0,0.05);
    }
`;


// --- ORIGINAL LOGIC & COMPONENTS ---

const VUMeterSection = ({ isPlaying, isMuted, volume }) => {
    const levelL = useMotionValue(0);
    const levelR = useMotionValue(0);

    useEffect(() => {
        let animationFrame;

        const updateMeters = () => {
            if (isPlaying && !isMuted) {
                const baseLevel = volume * 60;
                const noiseL = Math.random() * (volume * 40);
                const noiseR = Math.random() * (volume * 40);
                levelL.set(Math.min(100, Math.max(0, baseLevel + noiseL)));
                levelR.set(Math.min(100, Math.max(0, baseLevel + noiseR)));
                animationFrame = requestAnimationFrame(updateMeters);
            } else {
                const currentL = levelL.get();
                const currentR = levelR.get();
                if (currentL < 1 && currentR < 1) {
                    levelL.set(0);
                    levelR.set(0);
                } else {
                    levelL.set(Math.max(0, currentL * 0.8));
                    levelR.set(Math.max(0, currentR * 0.8));
                    animationFrame = requestAnimationFrame(updateMeters);
                }
            }
        };
        animationFrame = requestAnimationFrame(updateMeters);
        return () => cancelAnimationFrame(animationFrame);
    }, [isPlaying, isMuted, volume, levelL, levelR]);

    return (
        <div className="flex gap-3 p-3 bg-[#e0ded6] rounded-md shadow-inner border border-white/50">
            <VUMeter level={levelL} label="L" />
            <VUMeter level={levelR} label="R" />
        </div>
    );
};

const MusicPlayer = ({ isMini = false }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(0.5);
    const [isMuted, setIsMuted] = useState(false);
    const [isReady, setIsReady] = useState(false);

    // Playlist State (Same as before)
    const [playlists, setPlaylists] = useState({});
    const [currentPlaylistId, setCurrentPlaylistId] = useState('poolsuite');
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);

    // Glitch State
    const [displayText, setDisplayText] = useState('');
    const [, setScrambling] = useState(false);

    // Progress State
    const [progress, setProgress] = useState({ played: 0, playedSeconds: 0 });
    const [duration, setDuration] = useState(0);

    const playerRef = useRef(null);

    // --- TUNING NEEDLE LOGIC ---
    // Map current playlist index to percentage (0-100) for needle position
    const playlistKeys = Object.keys(playlists);
    const totalChannels = Math.max(1, playlistKeys.length);
    const currentIndex = playlistKeys.indexOf(currentPlaylistId);
    const needlePos = totalChannels > 1 ? (currentIndex / (totalChannels - 1)) * 100 : 50;

    // Smooth needle animation


    useEffect(() => {
        // Animate needle to new position
        // We can use framer motion's animate function if we were outside render, 
        // but here we can just pass the value to the motion component or use Spring
    }, [needlePos]);


    // Fetch Data
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Query Playlists first to ensure all channels are captured
                const query = `*[_type == "playlist"] | order(title asc) {
                    _id,
                    title,
                    "tracks": *[_type == "track" && references(^._id)] | order(title asc) {
                        title, artist, "src": soundCloudUrl, coverImage
                    }
                }`;

                const data = await fetchWithCache(query);

                if (data) {
                    const formatted = data.reduce((acc, pl) => {
                        // Only include playlists that actually have tracks, or keep all?
                        // User wants to see them, so let's keep them even if empty for now, 
                        // or better yet, filter out empty ones if true "radio" behavior is desired.
                        // But for "why is it only picking up 3", showing empty ones helps debug.
                        if (pl.tracks && pl.tracks.length > 0) {
                            acc[pl._id] = {
                                title: pl.title,
                                tracks: pl.tracks.map(t => ({
                                    ...t,
                                    cover: t.coverImage ? urlFor(t.coverImage).width(200).auto('format').url() : null
                                }))
                            };
                        }
                        return acc;
                    }, {});

                    setPlaylists(formatted);
                }
            } catch (error) {
                console.error("[MusicPlayer] Error fetching media:", error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const keys = Object.keys(playlists);
        if (keys.length > 0 && !playlists[currentPlaylistId]) {
            setCurrentPlaylistId(keys[0]);
        }
    }, [playlists, currentPlaylistId]);

    const currentPlaylistObj = playlists[currentPlaylistId];
    const currentPlaylist = currentPlaylistObj?.tracks || [];
    const currentTrack = currentPlaylist[currentTrackIndex];

    // Glitch Effect Logic
    useEffect(() => {
        if (!currentPlaylistObj?.title) return;

        setScrambling(true);

        const targetText = currentPlaylistObj.title;
        const chars = '!@#$%^&*()_+<>[]{}|';
        let iterations = 0;

        const interval = setInterval(() => {
            setDisplayText(targetText.split('').map((char, index) => {
                if (index < iterations) return char;
                return chars[Math.floor(Math.random() * chars.length)];
            }).join(''));

            if (iterations >= targetText.length) {
                clearInterval(interval);
                setScrambling(false);
            }
            iterations += 1 / 2; // Speed of resolve
        }, 50);

        return () => clearInterval(interval);
    }, [currentPlaylistId, currentPlaylistObj]);


    // Handlers
    const handleEnded = () => nextTrack();
    const togglePlay = () => setIsPlaying(!isPlaying);
    const nextTrack = () => setCurrentTrackIndex((prev) => (prev + 1) % currentPlaylist.length);
    const prevTrack = () => setCurrentTrackIndex((prev) => (prev - 1 + currentPlaylist.length) % currentPlaylist.length);

    const switchPlaylist = (id) => {
        if (id === currentPlaylistId) return;
        setCurrentPlaylistId(id);
        setCurrentTrackIndex(0);
        setIsPlaying(true);
        setProgress({ played: 0, playedSeconds: 0 }); // Reset progress
    };

    // Time Formatter
    const formatTime = (seconds) => {
        if (!seconds) return "00:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    // --- SHARED PLAYER LOGIC (Hidden) ---
    const HiddenPlayer = (
        <div style={{ width: '1px', height: '1px', overflow: 'hidden', position: 'absolute', opacity: 0, pointerEvents: 'none' }}>
            <ReactPlayer
                ref={playerRef}
                key={currentTrack?.src}
                url={currentTrack?.src || ''}
                playing={isPlaying && isReady && !!currentTrack?.src}
                playsinline={true}
                volume={isMuted ? 0 : volume}
                onReady={() => setIsReady(true)}
                onEnded={handleEnded}
                onProgress={setProgress}
                onDuration={setDuration}
                width="300px"
                height="300px"
                config={{ soundcloud: { options: { visual: false, auto_play: false } } }}
            />
        </div>
    );



    return (
        <Container>
            {/* === MOBILE LAYOUT (Unchanged) === */}
            <MobileWrapper>
                <div className="w-full h-full flex flex-col font-sans select-none p-2">
                    <div className="flex justify-between items-center mb-4 px-1">
                        <div className="flex space-x-1">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="w-1 h-1 rounded-full bg-black/20 shadow-[inset_0_1px_1px_rgba(0,0,0,0.2)]"></div>
                            ))}
                        </div>
                        <img src={logo} alt="Welcome to Paradise" className="h-8 object-contain opacity-80 mix-blend-multiply" />
                    </div>
                    <div className="bg-[#e8e6df] rounded p-3 mb-4 relative shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)] border-b border-white/50">
                        <div className="flex justify-between items-start mb-3">
                            <div className="overflow-hidden w-full mr-4">
                                <div className="font-bold text-sm truncate uppercase text-[#333333] tracking-wide">
                                    {currentTrack?.title || "No Track Selected"}
                                </div>
                                <div className="text-xs text-[#666666] truncate mt-0.5 font-medium">
                                    {currentTrack?.artist || "Unknown Artist"}
                                </div>
                            </div>
                            <div className="flex space-x-3 text-[#999999]">
                                <LinkIcon size={16} className="hover:text-black cursor-pointer transition-colors" />
                                <Heart size={16} className="hover:text-[#AF2E1B] cursor-pointer transition-colors" />
                            </div>
                        </div>
                        {/* Mobile VU Meter */}
                        <div className="flex justify-center bg-[#f4f1ea] p-2 rounded border border-[#999] shadow-inner">
                            <VUMeterSection isPlaying={isPlaying} isMuted={isMuted} volume={volume} />
                        </div>
                    </div>
                    <div className="flex items-end justify-between px-2 pb-2">
                        <div className="flex space-x-3 items-center mb-2">
                            <button onClick={togglePlay} className="w-16 h-16 rounded-lg bg-[#f0f0f0] flex items-center justify-center shadow-[inset_2px_2px_5px_rgba(0,0,0,0.1),inset_-2px_-2px_5px_rgba(255,255,255,0.8),0_1px_2px_rgba(0,0,0,0.1)] active:shadow-[inset_4px_4px_8px_rgba(0,0,0,0.15),inset_-4px_-4px_8px_rgba(255,255,255,0.5)] transition-all group">
                                <div className="w-12 h-12 rounded-full bg-[#f4f4f4] shadow-[2px_2px_4px_rgba(0,0,0,0.05),-2px_-2px_4px_rgba(255,255,255,0.8)] flex items-center justify-center group-active:scale-95 transition-transform">
                                    {isPlaying ? <Pause size={24} fill="#333" className="text-[#333]" /> : <Play size={24} fill="#333" className="text-[#333] ml-1" />}
                                </div>
                            </button>
                            <div className="flex flex-col space-y-2">
                                <button onClick={prevTrack} className="w-10 h-8 rounded bg-[#f0f0f0] flex items-center justify-center shadow-[inset_1px_1px_3px_rgba(0,0,0,0.1),inset_-1px_-1px_3px_rgba(255,255,255,0.8)] active:shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] transition-all">
                                    <SkipBack size={16} className="text-[#555]" />
                                </button>
                                <button onClick={nextTrack} className="w-10 h-8 rounded bg-[#f0f0f0] flex items-center justify-center shadow-[inset_1px_1px_3px_rgba(0,0,0,0.1),inset_-1px_-1px_3px_rgba(255,255,255,0.8)] active:shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] transition-all">
                                    <SkipForward size={16} className="text-[#555]" />
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-col items-center mx-4">
                            <div className="relative h-24 w-8 bg-[#dcdad3] rounded-full shadow-[inset_0_2px_4px_rgba(0,0,0,0.2)] flex justify-center py-2">
                                <div className="absolute top-2 bottom-2 w-0.5 bg-[#999]"></div>
                                <div className="absolute w-6 h-10 bg-[#e8e6df] rounded shadow-[0_2px_4px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.8)] cursor-ns-resize flex items-center justify-center" style={{ bottom: `${volume * 70}%` }} >
                                    <div className="w-4 h-0.5 bg-[#999] shadow-[0_1px_0_white]"></div>
                                </div>
                                <input type="range" min="0" max="1" step="0.05" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} onTouchStart={(e) => e.stopPropagation()} className="absolute w-24 h-8 opacity-0 cursor-ns-resize appearance-none z-10" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-90deg)' }} />
                            </div>
                            <span className="text-[9px] font-bold uppercase tracking-widest text-[#555] mt-1">Vol</span>
                        </div>
                        <div className="flex items-end space-x-4">
                            <Knob value={Object.keys(playlists).indexOf(currentPlaylistId)} min={0} max={Math.max(0, Object.keys(playlists).length - 1)} onChange={(val) => { const index = Math.round(val); const playlistKeys = Object.keys(playlists); if (playlistKeys[index]) switchPlaylist(playlistKeys[index]); }} label="Channel" size={56} />
                            <Switch isOn={!isMuted} onToggle={() => setIsMuted(!isMuted)} label="Audio" />
                        </div>
                    </div>
                </div>

            </MobileWrapper>


            {HiddenPlayer}

            {/* === DESKTOP VINTAGE RECEIVER (High Fidelity) === */}
            {isMini ? (
                <DesktopReceiver style={{ minWidth: 'auto', padding: '16px', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
                    {/* Screws (Mini) */}
                    <Screw style={{ top: 6, left: 6, width: 6, height: 6 }} rotate="45deg" />
                    <Screw style={{ top: 6, right: 6, width: 6, height: 6 }} rotate="-15deg" />
                    <Screw style={{ bottom: 6, left: 6, width: 6, height: 6 }} rotate="10deg" />
                    <Screw style={{ bottom: 6, right: 6, width: 6, height: 6 }} rotate="90deg" />

                    {/* Left: Play/Pause */}
                    <div className="flex-shrink-0">
                        <button onClick={togglePlay} className="w-12 h-12 rounded-full bg-[#f4f4f4] border border-[#ccc] shadow-[2px_2px_5px_rgba(0,0,0,0.1),-1px_-1px_2px_rgba(255,255,255,0.8)] flex items-center justify-center active:bg-[#e0e0e0] active:shadow-inner transition-all">
                            {isPlaying ? <Pause size={18} fill="#333" className="text-[#333]" /> : <Play size={18} fill="#333" className="text-[#333] ml-0.5" />}
                        </button>
                    </div>

                    {/* Center: Track Info */}
                    <div className="flex-1 flex flex-col justify-center overflow-hidden">
                        <div className="font-bold text-xs truncate text-[#333] tracking-wide">
                            {currentTrack?.title || "Waiting for signal..."}
                        </div>
                        <div className="text-[10px] text-[#666] truncate mt-0.5 font-medium">
                            {currentTrack?.artist || "Paradise FM"}
                        </div>
                    </div>

                    {/* Right: Controls */}
                    <div className="flex items-center gap-3">
                        <button onClick={prevTrack} className="p-1.5 rounded bg-[#e8e6df] shadow hover:bg-white active:scale-95 transition-all text-[#555]">
                            <SkipBack size={14} />
                        </button>
                        <button onClick={nextTrack} className="p-1.5 rounded bg-[#e8e6df] shadow hover:bg-white active:scale-95 transition-all text-[#555]">
                            <SkipForward size={14} />
                        </button>
                    </div>
                </DesktopReceiver>
            ) : (
                <DesktopReceiver>
                    {/* Screws */}
                    <Screw style={{ top: 12, left: 12 }} rotate="45deg" />
                    <Screw style={{ top: 12, right: 12 }} rotate="-15deg" />
                    <Screw style={{ bottom: 12, left: 12 }} rotate="10deg" />
                    <Screw style={{ bottom: 12, right: 12 }} rotate="90deg" />

                    {/* Vents - Subtle Texture */}
                    <VentGrill>
                        {[...Array(15)].map((_, i) => <VentSlot key={i} />)}
                    </VentGrill>

                    {/* 1. TUNER WINDOW */}
                    <TunerDisplay>
                        <TunerGlass />
                        {/* Scale Area */}
                        <TunerScale>
                            <ScaleLines>
                                {[...Array(21)].map((_, i) => (
                                    <ScaleTick key={i} major={i % 5 === 0} active={true} />
                                ))}
                            </ScaleLines>
                            {/* Needle */}
                            <TunerNeedle
                                animate={{ left: `${needlePos}%` }}
                                transition={{ type: "spring", stiffness: 60, damping: 15 }}
                            />
                        </TunerScale>

                        {/* Digital Readout */}
                        <TunerInfo>
                            <div className="flex items-center gap-4">
                                <span className="opacity-60 text-xs">FM</span>
                                <motion.span
                                    key={currentPlaylistId}
                                    initial={{ opacity: 0.5 }}
                                    animate={{ opacity: 1 }}
                                    className="text-xl font-bold tracking-widest"
                                >
                                    {100 + (currentIndex * 1.5)} MHz
                                </motion.span>
                            </div>

                            <div className="flex items-center gap-4">
                                {/* Track Timer */}
                                <div className="font-mono text-xs text-[#555] tracking-widest">
                                    {formatTime(progress.playedSeconds)} <span className="opacity-40">/</span> {formatTime(duration)}
                                </div>

                                {displayText && (
                                    <span className="uppercase tracking-wide text-[#00cc88] opacity-80 text-[10px] hidden sm:inline-block">
                                        {displayText}
                                    </span>
                                )}
                                <div className="flex gap-1 items-center">
                                    <div className={`w-1.5 h-1.5 rounded-full ${isPlaying ? 'bg-red-500 shadow-[0_0_5px_red]' : 'bg-[#bbb]'}`}></div>
                                    <span className="text-[9px] opacity-50 tracking-wider font-bold">STEREO</span>
                                </div>
                            </div>
                        </TunerInfo>
                    </TunerDisplay>

                    {/* 2. CONTROL PANEL LAYOUT */}
                    <ControlPanel>
                        {/* SECTION 1: OUTPUT (Levels) - Positioned Top Left */}
                        <PanelSection>
                            <div className="w-full flex justify-center mt-2">
                                <VUMeterSection isPlaying={isPlaying} isMuted={isMuted} volume={volume} />
                            </div>
                            <div className="text-[10px] font-bold tracking-[0.2em] text-[#666] uppercase mt-2">Output Level</div>
                        </PanelSection>

                        {/* SECTION 2: TRANSPORT & MODE - Positioned Bottom (Full Width) */}
                        <PanelSection>
                            <div className="flex items-center justify-center h-full gap-12 w-full">

                                {/* Toggle Switch */}
                                <Switch
                                    isOn={!isMuted}
                                    onToggle={() => setIsMuted(!isMuted)}
                                    label="POWER"
                                />

                                {/* Main Transport */}
                                <div className="flex items-center gap-6">
                                    <button onClick={prevTrack} className="w-12 h-12 rounded-full bg-[#f0f0f0] border border-[#d1d1d1] flex items-center justify-center shadow-[2px_2px_5px_rgba(0,0,0,0.05),-1px_-1px_2px_rgba(255,255,255,1)] active:shadow-inner hover:bg-white transition-all">
                                        <SkipBack size={18} className="text-[#555]" />
                                    </button>

                                    <button onClick={togglePlay} className="w-20 h-20 rounded-full bg-gradient-to-br from-[#f8f8f8] to-[#e0ded6] border border-[#bbb] flex items-center justify-center shadow-[5px_5px_15px_rgba(0,0,0,0.1),-3px_-3px_8px_rgba(255,255,255,0.9),inset_0_1px_2px_white] active:shadow-[inset_3px_3px_8px_rgba(0,0,0,0.1)] active:scale-[0.98] transition-all group relative">
                                        {/* Concave Dish */}
                                        <div className="absolute inset-3 rounded-full bg-gradient-to-tl from-[#dad8d0] to-[#f4f4f4] shadow-inner pointer-events-none"></div>
                                        <div className="relative z-10 opacity-80 group-hover:opacity-100 transition-opacity">
                                            {isPlaying ? <Pause fill="#333" className="text-[#333]" size={28} /> : <Play fill="#333" className="text-[#333] ml-1.5" size={28} />}
                                        </div>
                                    </button>

                                    <button onClick={nextTrack} className="w-12 h-12 rounded-full bg-[#f0f0f0] border border-[#d1d1d1] flex items-center justify-center shadow-[2px_2px_5px_rgba(0,0,0,0.05),-1px_-1px_2px_rgba(255,255,255,1)] active:shadow-inner hover:bg-white transition-all">
                                        <SkipForward size={18} className="text-[#555]" />
                                    </button>
                                </div>
                            </div>
                        </PanelSection>

                        {/* SECTION 3: TUNING & VOLUME - Positioned Top Right */}
                        <PanelSection>
                            <div className="flex items-end justify-center w-full h-full gap-8">
                                {/* Volume Fader */}
                                <div className="flex flex-col items-center">
                                    <div className="relative h-28 w-10 bg-[#dcdad3] rounded shadow-[inset_0_1px_3px_rgba(0,0,0,0.15)] flex justify-center py-3 border border-white/60">
                                        {/* Ruler */}
                                        <div className="absolute left-1.5 top-3 bottom-3 flex flex-col justify-between">
                                            {[...Array(9)].map((_, i) => (
                                                <div key={i} className="w-1.5 h-px bg-[#aaa]"></div>
                                            ))}
                                        </div>
                                        {/* Track Slot */}
                                        <div className="absolute top-3 bottom-3 w-1 bg-[#222]/10 rounded-full"></div>
                                        {/* Handle */}
                                        <div
                                            className="absolute w-7 h-10 bg-gradient-to-b from-[#444] to-[#222] rounded-[2px] shadow-[0_3px_6px_rgba(0,0,0,0.3)] cursor-ns-resize z-10 flex items-center justify-center border-t border-white/20"
                                            style={{ bottom: `${volume * 60 + 10}%` }}
                                        >
                                            <div className="w-4 h-px bg-white/40 mb-0.5"></div>
                                            <div className="w-4 h-px bg-white/40 mt-0.5"></div>
                                        </div>
                                        <input type="range" min="0" max="1" step="0.05" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} className="absolute opacity-0 cursor-ns-resize appearance-none z-20 w-28 h-10" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-90deg)' }} />
                                    </div>
                                    <span className="text-[9px] font-bold text-[#666] mt-2 tracking-widest">VOL</span>
                                </div>

                                {/* Tuning Knob */}
                                <Knob
                                    variant="tactile"
                                    snap={true}
                                    value={Object.keys(playlists).indexOf(currentPlaylistId)}
                                    min={0}
                                    max={Math.max(0, Object.keys(playlists).length - 1)}
                                    onChange={(val) => {
                                        const index = Math.round(val);
                                        const playlistKeys = Object.keys(playlists);
                                        if (playlistKeys[index]) switchPlaylist(playlistKeys[index]);
                                    }}
                                    label="TUNE"
                                    size={64}
                                />
                            </div>
                        </PanelSection>
                    </ControlPanel>
                </DesktopReceiver>
            )}

        </Container>
    );
};

export default MusicPlayer;
